import React from 'react'

import { VerifiedLicense } from 'src/shared/components/icons/verified-license'
import { useUserByIdSuspense } from 'src/shared/features/users/api/accounts/use-user-by-id'
import { usePublicLicenses } from 'src/shared/features/users/api/licenses/use-public-licenses'
import { type State } from 'src/shared/features/users/schemas/licenses'
import { statesMap } from 'src/shared/features/users/schemas/states'

interface PublicLicensesSectionProps {
  userId: number
}

export const PublicLicensesSection: React.FC<PublicLicensesSectionProps> = ({ userId }) => {
  const { data: publicLicenses } = usePublicLicenses(userId)
  const { data: user } = useUserByIdSuspense(userId)

  const isValidState = (stateCode: string): stateCode is State => {
    return Object.keys(statesMap).includes(stateCode)
  }

  if (!publicLicenses || publicLicenses.length === 0) return null
  const isLoanOfficer = user?.profession === 'mortgage_loan_officer'
  const validLicenses = publicLicenses.filter((license) => isValidState(license.state) && license.status === 'approved')
  const mainLicenseNumber = validLicenses.length > 0 ? validLicenses[0].licenseNumber || '' : ''

  if (validLicenses.length === 0) return null

  return (
    <div className="mt-4">
      {isLoanOfficer && mainLicenseNumber && (
        <div className="flex items-start gap-4">
          <VerifiedLicense className="mt-0.5 size-8 shrink-0 text-primary " />
          <div>
            <div className="flex items-center gap-1">
              <span className="text-lg font-bold">Verified Licenses</span>
              <span className="ml-1 text-muted">NMLS# {mainLicenseNumber}</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-1 text-muted">
              {validLicenses.slice(0, 10).map((license, index) => (
                <React.Fragment key={license.state}>
                  <span className="text-sm font-medium">
                    {statesMap[license.state].label}
                    {index < Math.min(validLicenses.length, 10) - 1 && ' · '}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {!isLoanOfficer && validLicenses.length > 0 && (
        <div className="flex items-start gap-4">
          <VerifiedLicense className="mt-0.5 size-8 shrink-0 text-primary" />
          <div>
            <p className="text-lg font-bold">Verified Licenses</p>
            <div className="flex flex-wrap gap-x-1">
              {validLicenses.map((license, index) => (
                <div key={license.state} className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-muted">
                    <span>{statesMap[license.state].label}</span>
                    {license.licenseNumber && <span className="text-sm font-light">({license.licenseNumber})</span>}
                    {index < validLicenses.length - 1 && <span className="ml-1">·</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PublicLicensesSection
